import { cn } from '@/lib/utils';
import { weekShortLabel } from '@/lib/week-label';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { PrizesShowcase } from '@/components/events/PrizeDisplay';
import { API_BASE } from '@/lib/api';
import { asset } from '@/lib/asset';
import type {
  MainEventDetail,
  LeaderboardEntry,
  SurvivorStandingRow,
  BestBallStandingRow,
} from '@/services/events/main-event';
import { FORMAT_DESCRIPTIONS } from '@/services/events/main-event/format-descriptions';
import { fmtPts } from './utils';

/**
 * Modal sheets opened from the compressed event header's tappable stats.
 * Follows the SurvivorPoolSheet pattern (Radix Modal, parent-controlled open).
 */

function rankBadgeClass(rank: number): string {
  return cn(
    'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold tabular-nums shrink-0',
    rank === 1
      ? 'bg-gold/20 text-gold'
      : rank === 2
        ? 'bg-tertiary/20 text-cream'
        : rank === 3
          ? 'bg-orange-500/20 text-orange-400'
          : 'bg-surface-3 text-tertiary',
  );
}

/** Leaderboard (standard) / standings (survivor, best ball) — off the page body. */
export function LeaderboardSheet({
  open,
  onClose,
  isSurvivor,
  isBestBall = false,
  leaderboard,
  survivorStandings,
  bestBallStandings = [],
  myUserId,
}: {
  open: boolean;
  onClose: () => void;
  isSurvivor: boolean;
  isBestBall?: boolean;
  leaderboard: LeaderboardEntry[];
  survivorStandings: SurvivorStandingRow[];
  bestBallStandings?: BestBallStandingRow[];
  /** Highlights the caller's row in the Best Ball standings. */
  myUserId?: string | null;
}) {
  const rows = isBestBall ? bestBallStandings : isSurvivor ? survivorStandings : leaderboard;
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <ModalContent className="max-w-md">
        <ModalHeader>
          <ModalTitle>{isSurvivor || isBestBall ? 'Standings' : 'Leaderboard'}</ModalTitle>
          <ModalDescription>
            {isBestBall
              ? 'Ranked by season points'
              : isSurvivor
                ? 'Ranked by weeks survived'
                : 'Ranked by total points'}
          </ModalDescription>
        </ModalHeader>

        {rows.length === 0 ? (
          <p className="text-sm text-tertiary text-center py-4">No entries yet</p>
        ) : (
          <div className="divide-y divide-surface-3 -mx-2">
            {isBestBall
              ? bestBallStandings.map((entry) => (
                  <div
                    key={entry.userId}
                    className={cn(
                      'flex items-center justify-between px-2 py-3',
                      myUserId && entry.userId === myUserId && 'bg-gold/5 rounded-lg',
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={rankBadgeClass(entry.rank)}>{entry.rank}</span>
                      <span className="text-sm text-cream truncate">{entry.username}</span>
                      {myUserId && entry.userId === myUserId && (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-gold/15 text-gold shrink-0">
                          You
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-bold text-gold shrink-0 tabular-nums">
                      {fmtPts(entry.seasonPoints)}
                    </span>
                  </div>
                ))
              : isSurvivor
              ? survivorStandings.map((entry) => (
                  <div key={entry.userId} className="flex items-center justify-between px-2 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={rankBadgeClass(entry.rank)}>{entry.rank}</span>
                      <span className="text-sm text-cream truncate">{entry.username}</span>
                      <span
                        className={cn(
                          'px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider shrink-0',
                          entry.status === 'ALIVE'
                            ? 'bg-verdigris/15 text-verdigris'
                            : 'bg-oxblood/15 text-oxblood',
                        )}
                      >
                        {entry.status === 'ALIVE' ? 'Alive' : 'Out'}
                      </span>
                    </div>
                    <span
                      className={cn(
                        'text-sm font-bold tabular-nums shrink-0',
                        entry.status === 'ALIVE' ? 'text-gold' : 'text-warm-gray',
                      )}
                    >
                      {entry.status === 'ALIVE'
                        ? `Wk ${entry.weeksSurvived}`
                        : `Out ${weekShortLabel(entry.eliminatedWeek)}`}
                    </span>
                  </div>
                ))
              : leaderboard.map((entry) => (
                  <div key={entry.userId} className="flex items-center justify-between px-2 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={rankBadgeClass(entry.rank)}>{entry.rank}</span>
                      <span className="text-sm text-cream truncate">{entry.username}</span>
                    </div>
                    <div className="text-right flex items-center gap-2 shrink-0">
                      <span className="text-sm font-bold text-gold tabular-nums">{entry.totalScore}</span>
                      {entry.prizeAmount != null && entry.prizeAmount > 0 && (
                        <span className="text-xs text-amber-400/70 tabular-nums">
                          {entry.prizeAmount.toLocaleString()} coins
                        </span>
                      )}
                    </div>
                  </div>
                ))}
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}

/**
 * The full prize cabinet, opened from the header's Prize Pool stat — and
 * auto-opened once for a visitor who has not entered a prized event.
 *
 * On that auto-open path the sheet is the first thing a new visitor sees, so
 * it needs a way forward as well as a way out. Without `onEnter` it offered
 * only an X, and the one thing on it that looked clickable — a circled arrow
 * in the banner — was decorative, so the obvious next step led nowhere.
 */
export function PrizesSheet({
  open,
  onClose,
  event,
  onEnter,
  enterLabel = 'Enter Event',
  entering = false,
}: {
  open: boolean;
  onClose: () => void;
  event: MainEventDetail;
  /** Omitted when the viewer has already entered or entry is closed. */
  onEnter?: () => void;
  enterLabel?: string;
  entering?: boolean;
}) {
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <ModalContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <ModalHeader>
          <ModalTitle>Prizes</ModalTitle>
        </ModalHeader>
        <PrizesShowcase event={event} coinIconSrc={asset('assets/icons/coins.webp')} />
        {onEnter && (
          <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={onClose} className="sm:w-auto">
              Not now
            </Button>
            <Button
              variant="coins"
              onClick={() => {
                // Close first: entry can open its own modal (card leagues) or
                // route to login, and stacking those behind this sheet leaves
                // the user looking at prizes with a dialog they cannot see.
                onClose();
                onEnter();
              }}
              disabled={entering}
              className="sm:w-auto"
            >
              {entering ? 'Entering…' : enterLabel}
            </Button>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}

/** Hero art + long description, moved behind the title's About affordance. */
export function AboutSheet({
  open,
  onClose,
  event,
}: {
  open: boolean;
  onClose: () => void;
  event: MainEventDetail;
}) {
  // Every event shows a description behind the title's info icon: the admin's
  // custom copy when set, else the stock blurb for the event's format.
  const description = event.description || FORMAT_DESCRIPTIONS[event.format] || null;
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <ModalContent className="max-w-xl">
        <ModalHeader>
          <ModalTitle>{event.title}</ModalTitle>
        </ModalHeader>
        <div className="rounded-xl overflow-hidden border border-surface-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={event.imageUrl || `${API_BASE}/api/og/event/${event.id}`}
            alt={event.title}
            className="w-full h-auto"
          />
        </div>
        {description && (
          <p className="text-sm text-warm-gray whitespace-pre-line">{description}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-warm-gray">
          <span>
            {new Date(event.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {' — '}
            {new Date(event.endTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          <span>{event.undercardCount} weeks</span>
          <span>{event.entryCount} entered</span>
        </div>
      </ModalContent>
    </Modal>
  );
}
